import React, { createContext, useContext, useState, useEffect, useLayoutEffect, ReactNode } from 'react';
import { StorageKeys } from '../constants/storageKeys';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  forceLight?: boolean;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, forceLight = false }) => {
  // Theme state tracks actual user preference; forceLight only removes the CSS class without touching the stored value
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem(StorageKeys.THEME) as Theme | null;
    if (savedTheme) return savedTheme;
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    return 'light';
  });

  // When forceLight is lifted, re-sync from localStorage in case it drifted
  useEffect(() => {
    if (!forceLight) {
      const savedTheme = localStorage.getItem(StorageKeys.THEME) as Theme | null;
      if (savedTheme) setTheme(savedTheme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceLight]);

  // useLayoutEffect updates DOM before paint to prevent flash
  useLayoutEffect(() => {
    const root = document.documentElement;

    if (forceLight) {
      root.classList.remove('dark');
      return;
    }

    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    localStorage.setItem(StorageKeys.THEME, theme);
  }, [theme, forceLight]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-switch if user hasn't set a manual preference
      const savedTheme = localStorage.getItem(StorageKeys.THEME);
      if (!savedTheme) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isDark: theme === 'dark' }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
